'use client'

import { useCallback, useMemo } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

type ParamInput = string | readonly string[] | null | undefined

export function useQueryParams() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const params = useMemo(() => {
    const result: Record<string, string | string[]> = {}
    if (!searchParams) return result

    for (const key of Array.from(searchParams.keys())) {
      const values = searchParams.getAll(key)
      result[key] = values.length > 1 ? values : values[0]
    }

    return result
  }, [searchParams])

  const buildSearch = useCallback(
    (overrides?: Record<string, ParamInput>) => {
      const next = new URLSearchParams()

      // start from current params
      if (searchParams) {
        for (const key of Array.from(searchParams.keys())) {
          const vals = searchParams.getAll(key)
          for (const v of vals) next.append(key, v)
        }
      }

      // apply overrides: set/clear
      if (overrides) {
        for (const [k, v] of Object.entries(overrides)) {
          next.delete(k)
          if (v == null) continue

          const values: string[] = Array.isArray(v)
            ? (v as readonly string[]).map(String)
            : [String(v)]

          for (const item of values) next.append(k, item)
        }
      }

      const s = next.toString()
      return s ? `?${s}` : ''
    },
    [searchParams],
  )

  const set = useCallback(
    (key: string, value: ParamInput) => {
      const query = buildSearch({ [key]: value })

      router.replace(`${pathname}${query}`)
    },
    [buildSearch, router, pathname],
  )

  const setMany = useCallback(
    (values: Record<string, ParamInput>) => {
      const query = buildSearch(values)

      router.replace(`${pathname}${query}`)
    },
    [buildSearch, router, pathname],
  )

  const remove = useCallback(
    (key: string) => {
      const query = buildSearch({ [key]: null })
      router.replace(`${pathname}${query}`)
    },
    [buildSearch, router, pathname],
  )

  const get = useCallback(
    (key: string) => {
      if (!searchParams) {
        return undefined
      }

      return searchParams.get(key) ?? undefined
    },
    [searchParams],
  )

  const getAll = useCallback(
    (key: string) => {
      if (!searchParams) return []
      return searchParams.getAll(key)
    },
    [searchParams],
  )

  return {
    params,
    get,
    getAll,
    set,
    setMany,
    remove,
  }
}
